<template>
  <v-chip
    class="validity-counter"
    :class="{
      'validity-red': showValidityOutline && isActive && isInvalid,
      'validity-green': showValidityOutline && isActive && !isInvalid,
      'validity-neutral': isNeutral || !showValidityOutline,
      'validity-flagged': paintInvalid && showValidityOutline,
      'validity-disabled': disabled,
    }"
    @click="onClickCounter"
    label
    :ripple="false"
  >
    <div v-if="showCapacityIcon" class="capacity-icon-wrapper">
      <CapacityIcon
        :capacity="capacity"
        :allocation="allocation"
        size="normal"
        :allow_change="false"
      />
    </div>

    <!-- Direct Responsibility segment (redesign, gated by idea-MDP-585) -->
    <div v-if="showRoleResponsibilityCounts && directSegmentVisible" class="validity-wrapper direct-segment">
      <span class="dash-icon first-dash" :class="{ 'dash-icon-green': !directIsNotActive }">
        -
      </span>
      <Icon
        name="org_icon_responsibility"
        class="validity-icon1"
        :class="{ 'direct-icon-red': directIsNotActive }"
      />
      <span
        class="validity-label direct-count-label"
        :class="directIsNotActive ? 'count-label-red' : 'count-label-green'"
      >
        {{ directCount }}
      </span>
    </div>

    <!-- Divider between the Direct Responsibility and Role/Responsibility segments -->
    <span v-if="showRoleResponsibilityCounts && showDivider" class="slash-divider">/</span>

    <!-- Invalid roles segment (red warning count) -->
    <div v-if="showRoleResponsibilityCounts && showInvalid" class="validity-wrapper">
      <span
        class="dash-icon"
        :class="{
          'first-dash': !directSegmentVisible,
          'dash-icon-black': invalidRolesCount > 0 && validRolesCount > 0,
          'dash-icon-neutral': invalidSegmentNeutral,
        }"
      >
        -
      </span>
      <Icon
        name="org_icon_role"
        class="validity-icon"
        :class="{
          'validity-label-neutral': invalidSegmentNeutral,
        }"
      />
      <span
        class="validity-label"
        :class="invalidSegmentNeutral ? 'count-label-neutral' : 'count-label-red'"
      >
        {{ invalidRolesCount }}
      </span>
    </div>

    <!-- Valid roles segment -->
    <div v-if="showRoleResponsibilityCounts && showValid" class="validity-wrapper">
      <span
        class="dash-icon"
        :class="{
          'dash-icon-green': validRolesCount > 0,
          'dash-icon-black': invalidRolesCount > 0 && validRolesCount > 0,
          'first-dash': !directSegmentVisible && invalidRolesCount === 0 && validRolesCount > 0,
        }"
      >
        -
      </span>
      <Icon
        name="org_icon_role"
        class="validity-icon"
        :class="{
          'validity-label-green': validRolesCount > 0,
        }"
      />
      <span
        class="validity-label"
        :class="{
          'count-label-green': validRolesCount > 0,
        }"
      >
        {{ validRolesCount }}
      </span>
    </div>

    <!-- Responsibilities via Roles segment -->
    <div v-if="showRoleResponsibilityCounts && showResp" class="validity-wrapper">
      <span
        class="dash-icon"
        :class="{
          'dash-icon-green': validRolesCount > 0,
        }"
      >
        -
      </span>
      <Icon name="org_icon_responsibility" class="validity-icon1" />
      <span class="validity-label validity-res-count count-label-green">
        {{ validResponsibilitiesCount }}
      </span>
    </div>

    <!-- Empty state: no roles and no direct responsibilities -->
    <div v-if="showRoleResponsibilityCounts && showPlaceholder" class="validity-wrapper">
      <span class="dash-icon first-dash" :class="{ 'dash-icon-neutral': isNeutral }">
        -
      </span>
      <Icon
        name="org_icon_role"
        class="validity-icon"
        :class="{ 'validity-label-neutral': isNeutral }"
      />
      <span
        class="validity-label"
        :class="isNeutral ? 'count-label-neutral' : 'count-label-red'"
      >
        0
      </span>
    </div>
  </v-chip>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import Icon from "@components/icons/Icon.vue";
import CapacityIcon from "@components/organizations/positions/PositionCapacityIcon.vue";
import { LD_FLAG_IDEA_MDP_585 } from "@components/constants/launchDarklyFlagKeys";
import { INVALIDITY_REASON_POSITION_HAS_INVALID_ACTIVE_ROLE } from "@components/constants/invalidityReasons";

const props = defineProps({
  status: {
    type: String,
    default: "active",
  },
  isInvalid: {
    type: Boolean,
    required: true,
  },
  directResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  notActiveDirectResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  validRolesCount: {
    type: Number,
    default: 0,
  },
  invalidRolesCount: {
    type: Number,
    default: 0,
  },
  validResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  capacity: {
    type: Number,
    default: 0,
  },
  allocation: {
    type: String,
    default: "not",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Hide the leading capacity icon when the capacity is shown elsewhere (Position Card capacity trigger). */
  showCapacityIcon: {
    type: Boolean,
    default: true,
  },
  /** When false (product Readiness off), hide role/responsibility counter pills and validity outline. */
  showRoleResponsibilityCounts: {
    type: Boolean,
    default: true,
  },
  /** ER-9940: paint the red validity border (payload `is_valid === false`, resolved by the parent). */
  paintInvalid: {
    type: Boolean,
    default: false,
  },
  /** ER-9940: payload `invalidity_reason`; the red invalid-role count is shown for POSITION_HAS_INVALID_ACTIVE_ROLE. */
  invalidityReason: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["click"]);

const showRoleResponsibilityCounts = computed(() => props.showRoleResponsibilityCounts !== false);
const showValidityOutline = computed(() => showRoleResponsibilityCounts.value);

const isActive = computed(() => (props.status || "active").toLowerCase() === "active");

// Outline is never recomputed locally: the backend's `is_invalid` is the single
// validity predicate, and it only applies to Active positions. Non-active positions
// (Draft / Retired / Archived) are always neutral.
const isNeutral = computed(() => !isActive.value);

const directSegmentEnabled = ref(false);
const proxy = getCurrentInstance()?.proxy;

onMounted(async () => {
  try {
    directSegmentEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_585, false)) === true;
  } catch (error) {
    directSegmentEnabled.value = false;
  }
});

const directIsNotActive = computed(() => props.notActiveDirectResponsibilitiesCount > 0);

const directCount = computed(() =>
  directIsNotActive.value
    ? props.notActiveDirectResponsibilitiesCount
    : props.directResponsibilitiesCount
);

const hasDirectResponsibilities = computed(
  () => props.directResponsibilitiesCount > 0 || props.notActiveDirectResponsibilitiesCount > 0
);

const directSegmentVisible = computed(
  () => directSegmentEnabled.value && hasDirectResponsibilities.value
);

const showInvalid = computed(() => props.invalidRolesCount > 0);

const invalidRoleReasonActive = computed(
  () =>
    props.paintInvalid && props.invalidityReason === INVALIDITY_REASON_POSITION_HAS_INVALID_ACTIVE_ROLE
);
const invalidSegmentNeutral = computed(() => isNeutral.value && !invalidRoleReasonActive.value);
const showValid = computed(() => props.validRolesCount > 0);
const showResp = computed(() => props.validResponsibilitiesCount > 0);

const showPlaceholder = computed(
  () =>
    !directSegmentVisible.value && !showInvalid.value && !showValid.value && !showResp.value
);

const showDivider = computed(
  () =>
    directSegmentVisible.value && (showInvalid.value || showValid.value || showResp.value)
);

function onClickCounter(event) {
  // Forward the DOM event so parent listeners can use modifiers (e.g. @click.stop).
  emit("click", event);
}
</script>

<style scoped>
.validity-counter {
  height: 28px !important;
  padding: 2px 6px 4px 6px;
  border: 2px solid;
  border-radius: 5px !important;
}
.validity-counter:hover:before {
  opacity: 0;
}
.validity-counter:active {
  box-shadow: none;
}
.validity-counter:focus:before {
  opacity: 0;
}
.validity-red {
  background-color: #ffe0e0 !important;
  border-color: #d92d20 !important;
}
.validity-neutral {
  background-color: #f9fafb !important;
  border-color: #eaecf0 !important;
}
.validity-green {
  background-color: #ffffff !important;
  border-color: #128e55 !important;
}
/* ER-9940: red validity border; white fill keeps the red counts above 4.5:1 (#d92d20 on #fff = 4.83:1). */
.validity-flagged {
  background-color: #ffffff !important;
  border-color: #d92d20 !important;
}
.position-badge-icon {
  margin-top: 2px;
  margin-right: 5px;
  height: 20px;
  margin-left: -4px;
}
.validity-counter .validity-wrapper {
  display: flex;
  align-items: center;
}
.validity-counter .validity-icon {
  position: relative;
  left: -1px;
  top: 0.2px;
}
::v-deep.validity-counter .validity-icon svg path {
  fill: #d92d20;
  stroke: #d92d20;
}
::v-deep.validity-counter .validity-label-neutral svg path {
  fill: #344054;
  stroke: #344054;
}
::v-deep.validity-counter .validity-label-green svg path {
  fill: #128e55;
  stroke: #128e55;
}
::v-deep.validity-counter .validity-icon1 svg path {
  fill: #128e55;
}
::v-deep.validity-counter .direct-icon-red svg path {
  fill: #d92d20;
  stroke: #d92d20;
}
.validity-counter .validity-icon1 {
  position: relative;
  left: 2px;
}
.validity-counter .validity-icon {
  position: relative;
  left: 3px;
}
.validity-counter .validity-label {
  font-family: Inter;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0%;
  text-align: center;
  margin-left: 4px;
  margin-top: 3px;
}
.validity-counter .count-label-red {
  color: #d92d20;
}
.validity-counter .count-label-neutral {
  color: #344054;
}
.validity-counter .count-label-green {
  color: #128e55;
}
.validity-counter .validity-label {
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  margin-left: 7px;
  margin-top: 3px;
}
.validity-res-count {
  margin-left: 3px !important;
}
.direct-count-label {
  margin-left: 7px !important;
}
.dash-icon {
  font-family: Inter;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
  color: #d92d20;
  margin-left: 5px;
}
.dash-icon-green {
  color: #128e55;
}
.dash-icon-black {
  color: #344054;
}
.dash-icon-neutral {
  color: #344054;
}
.first-dash {
  margin-left: 0px;
}
.slash-divider {
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #666666;
  margin-left: 6px;
  margin-right: 6px;
  margin-top: 3px;
}
.validity-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.capacity-icon-wrapper {
  position: relative;
  top: 2px;
  left: -4.5px;
}
::v-deep.capacity-icon-wrapper .capacity_icon_normal_size {
  width: 22px;
  height: 22px;
}
::v-deep.capacity-icon-wrapper .capacity_icon_normal_size .normal_icon_size {
  background-size: 15px 15px;
}
</style>
